import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { include, exclude } from "@insightfulscience/shared-utils/lists/set";
import { compose } from "@insightfulscience/shared-utils/fn";
import { memo1 } from "@insightfulscience/shared-utils/memo";
import { targetValue } from "../events/readers";
import { setRef } from "../events/handlers";
import useUnmounted, { useIfMounted } from "../hooks/useUnmounted";

const merge = payload => state => ({ ...state, ...payload });

const pack = field => value => ({ [field]: value });

const inc = (x, delta = 1) => x + delta;

const toErrorObject = errors => field =>
	errors[field] // is field invalid
		? { [field]: errors[field] } // creating an object
		: null;

const fieldsToErrors = (fields, errors) =>
	Object.assign(
		{}, // esnure result not null
		...fields.map(toErrorObject(errors)),
	);

const findInvalidControl = (controls, errors) =>
	Array.from(controls).find(([field, element]) => errors[field] && element);

const add = value => list => include(list, value);
const del = value => list => exclude(list, value);

function useSmartForm(initialState, validateForm, formAction, stateRef, options) {
	const isUnmountedRef = useUnmounted();
	const [form, setFormStateUnsafe] = useState(() => initialState);
	const [validations, setValidationsUnsafe] = useState(0);
	const [touched, setTouchedUnsafe] = useState([]);
	const validationResult = useRef();
	const controls = useRef(new Map());

	const setFormState = useIfMounted(setFormStateUnsafe, isUnmountedRef);
	const setValidations = useIfMounted(setValidationsUnsafe, isUnmountedRef);
	const setTouched = useIfMounted(setTouchedUnsafe, isUnmountedRef);

	setRef(stateRef, form);

	const optionsContext = useMemo(
		() => ({
			blurDelay: options?.blurDelay ?? null,
		}),
		[options],
	);

	const touch = useCallback(
		memo1(field => () => setTouched(add(field))), // callback
		[],
	);

	const untouch = useCallback(
		memo1(field => () => setTouched(del(field))),
		[],
	);

	const untouchForm = useCallback(
		() => setTouched([]), // callback
		[], // deps
	);

	const update = useMemo(
		() =>
			memo1((field, parseEvent = targetValue) =>
				typeof field === "function"
					? compose(setFormState, value => state => field(state, value), parseEvent)
					: compose(setFormState, merge, pack(field), parseEvent),
			),
		[],
	);

	const register = useMemo(
		() =>
			memo1(field => element => {
				controls.current.set(field, element);
			}),
		[],
	);

	const resetValidations = useCallback(
		() => setValidations(0), // callback
		[],
	);

	validationResult.current = useMemo(
		() => validateForm(form),
		[form, validateForm], // dependencies
	);

	validationResult.current.state = form;

	const { isValid, errors } = validationResult.current;

	const touchedErrors = useMemo(
		() => fieldsToErrors(touched, errors),
		[errors, touched], // dependency
	);

	const visibleErrors = validations > 0 ? errors : touchedErrors;

	const submit = useCallback(event => {
		const formState = validationResult.current;
		event.preventDefault();
		if (!formState.isValid) {
			setValidations(inc);
			return;
		}
		resetValidations();
		untouchForm();
		formAction(formState.state);
	}, []);

	const reset = useCallback(() => {
		resetValidations();
		untouchForm();
		setFormState(initialState);
	}, [initialState]);

	useEffect(() => {
		if (validations === 0) return;
		const [, control] = findInvalidControl(controls.current, errors) || [];
		if (control == null) return;
		control.focus();
		if (control.offsetParent === null) control.scrollIntoView();
	}, [validations]);

	return {
		form, // { [field: string]: T } - Object contains form fields
		setState: setFormState, // Function updates form state
		isValid, // boolean; - Flag On if form is valid
		formErrors: errors, // { [field: string]: string|string[] } - Object with all errors
		errors: visibleErrors, // { [field: string]: string|string[] } - only visible errors
		resetValidations, // Functions reset validations
		touch, // Function returns callback that mark specific field touched
		untouch, // Function returns callback that mark specific field untouched
		untouchForm, // Function marks all fields untouched
		register, // Functions returns callback that registed DOM-element
		update, // Function returns callback that parses event and updates the value of specific field
		submit, // Function that submits form
		touched, // list of touched field names
		reset, // Function resets form state (including form data)
		options: optionsContext, // The options to be processed by the fields
	};
}

export default useSmartForm;
